import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import TeamsTable from '../../../Common/TeamsTable';
import TeamsHeader from '../../../Common/TeamsHeader';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { DeleteModal } from '../../../Common/DeleteModal';
import Select from 'react-select';
import imageCompression from 'browser-image-compression';
import secureApi from '../../../utils/secureApi';
import { Team } from '../../../types/Team';
import { getTeamsByClubId, TeamData, createTeam, getAgeCategoriesFromTournaments, CreateTeamRequest, getTeamById, updateTeam, TeamDetails, deleteTeam } from '../../../api/club/teamService';
import { getUserClubIds } from '../../../utils/roleBasedAccess';
import './TeamInformation.css';
import '../../../components/PlayerComponents.css';
import { getImageUrl } from '../../../utils/imageUrl';





const TeamInformation = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);



  const [teamFormData, setTeamFormData] = useState({
    name: '',
    shortName: '',
    logo: '',
    ageGroups: [] as string[],
    coachManagerName: '',
    contactDetails: '',
    sports: 'Football',
    status: 'active'
  });

  const [ageCategories, setAgeCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);

  // Add state to track original form data for change detection
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    shortName: '',
    logo: '',
    ageGroups: [] as string[],
    coachManagerName: '',
    contactDetails: '',
    sports: 'Football',
    status: 'active'
  });

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalTeam, setDeleteModalTeam] = useState<Team | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);


  useEffect(() => {
    loadTeams();
    loadAgeCategories();
  }, []);

  // Debug useEffect to monitor teamFormData changes
  useEffect(() => {
    if (showTeamForm && editingTeam) {
      console.log('teamFormData changed:', teamFormData);
      console.log('teamFormData.ageGroups:', teamFormData.ageGroups);
      console.log('teamFormData.ageGroups type:', typeof teamFormData.ageGroups);
      console.log('Is teamFormData.ageGroups array:', Array.isArray(teamFormData.ageGroups));
    }
  }, [teamFormData, showTeamForm, editingTeam]);

  const loadAgeCategories = async () => {
    try {
      const categories = await getAgeCategoriesFromTournaments();
      setAgeCategories(categories);
    } catch (error) {
      console.error('Failed to load age categories:', error);
      // Set default categories if API fails
      setAgeCategories(['U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18']);
    }
  };

  const loadTeams = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get user's club IDs
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      
      if (clubIds.length === 0) {
        setError('No club access found for this user. Please contact your administrator.');
        setLoading(false);
        return;
      }
      
      // Use the first club ID (assuming single club access for now)
      const clubId = clubIds[0];
      
      // Fetch teams from API
      const teamsData: TeamData[] = await getTeamsByClubId(clubId);
      
      console.log('Raw API teams data:', teamsData);
      
      // Transform API data to match the Team interface
      const transformedTeams: Team[] = teamsData.map(team => ({
        _id: team._id,
        name: team.name,
        shortName: team.shortName,
                    logo: team.logo || '',
        ageGroups: [team.ageCategory], // Convert single ageCategory to array
        coachManagerName: '', // Not provided by API in list response
        contactDetails: '', // Not provided by API in list response
        players: team.playersCount,
        verifiedPlayers: team.approvedPlayersCount,
        paidPlayers: team.approvedPlayersCount, // Assuming approved players are paid
        sports: 'Football', // Default value
        status: 'active', // Default value
        isSelected: false
      }));
      
      console.log('Transformed teams data:', transformedTeams);
      
      setTeams(transformedTeams);
    } catch (error: any) {
      console.error('Error loading teams:', error);
      setError(error.message || 'Failed to load teams. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeam = () => {
    setEditingTeam(null);
    setTeamFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: '',
      sports: 'Football',
      status: 'active'
    });
    setOriginalFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: '',
      sports: 'Football',
      status: 'active'
    });
    setLogoError(null);
    setIsLogoUploading(false);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);
    setShowTeamForm(true);
    
    // Scroll to top smoothly after form is shown
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleEditTeam = async (team: Team) => {
    try {
      setSubmitError(null);
      setShowTeamForm(true);
      setIsEditLoading(true);
      
      // Show loading state for form
      setTeamFormData({
        name: '',
        shortName: '',
        logo: '',
        ageGroups: [],
        coachManagerName: '',
        contactDetails: '',
        sports: 'Football',
        status: 'active'
      });
      
      // Try to fetch detailed team information by ID
      let teamDetails: TeamDetails | null = null;
      try {
        teamDetails = await getTeamById(team._id);
        console.log('Team details fetched from API:', teamDetails);
        console.log('Age category from API:', teamDetails.ageCategory);
        console.log('Age category type:', typeof teamDetails.ageCategory);
        console.log('Is age category array:', Array.isArray(teamDetails.ageCategory));
      } catch (apiError: any) {
        console.warn('API failed, using local team data:', apiError.message);
        // Show a warning message to user
        setSubmitError(`Note: Using cached team data. ${apiError.message}`);
        // Fallback to local team data
        console.log('Using fallback data. Team ageGroups:', team.ageGroups);
        console.log('Team ageGroups type:', typeof team.ageGroups);
        console.log('Is team ageGroups array:', Array.isArray(team.ageGroups));
        
        teamDetails = {
          _id: team._id,
          name: team.name,
          shortName: team.shortName,
          logo: team.logo || '',
          managerName: team.coachManagerName || '',
          countryCode: 91,
          managerContactNum: parseInt(team.contactDetails?.replace(/\D/g, '') || '0'),
          location: 'Bengaluru',
          ageCategory: Array.isArray(team.ageGroups) ? team.ageGroups : [],
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
      
      setEditingTeam(team);
      
      // Handle both string and array ageCategory from API
      let finalAgeGroups: string[] = [];
      if (Array.isArray(teamDetails.ageCategory)) {
        finalAgeGroups = teamDetails.ageCategory;
      } else if (typeof teamDetails.ageCategory === 'string') {
        const ageCategoryStr = teamDetails.ageCategory as string;
        if (ageCategoryStr.trim() !== '') {
          finalAgeGroups = [ageCategoryStr];
        }
      }
      console.log('Final ageGroups for form:', finalAgeGroups);
      
      const formData = {
        name: teamDetails.name || '',
        shortName: teamDetails.shortName || '',
        logo: teamDetails.logo || '',
        ageGroups: finalAgeGroups,
        coachManagerName: teamDetails.managerName || '',
        contactDetails: teamDetails.managerContactNum ? teamDetails.managerContactNum.toString() : '',
        sports: 'Football', // Default value as it's not in the API response
        status: 'active' // Default value as it's not in the API response
      };
      
      console.log('Setting form data:', formData);
      console.log('Form data ageGroups:', formData.ageGroups);
      
      setTeamFormData(formData);
      setOriginalFormData(formData); // Set original data
      
      setLogoError(null);
      setIsLogoUploading(false);
      setLogoUploadProgress(0);
      setLogoUploadSuccess(false);
      
      // Scroll to top smoothly after form is shown
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    } catch (error: any) {
      console.error('Failed to handle edit team:', error);
      setSubmitError(error.message || 'Failed to load team details');
      setShowTeamForm(false);
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSaveTeam = async () => {
    if (editingTeam) {
      // Update existing team via API
      await updateExistingTeam();
    } else {
      // Create new team via API
      await createNewTeam();
    }
  };

  const updateExistingTeam = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      if (!editingTeam) {
        throw new Error('No team selected for editing');
      }

      // Prepare team data for API
      const teamData = {
        name: teamFormData.name,
        shortName: teamFormData.shortName,
        logo: teamFormData.logo,
        managerName: teamFormData.coachManagerName,
        countryCode: 91, // Default to India
        managerContactNum: parseInt(teamFormData.contactDetails.replace(/\D/g, '')) || 0,
        location: 'Bengaluru', // Default location
        ageCategory: Array.isArray(teamFormData.ageGroups) && teamFormData.ageGroups.length > 0 
          ? teamFormData.ageGroups[0] // Take the first age category as string
          : ''
      };

      // Call API to update team
      const response = await updateTeam(editingTeam._id, teamData);

      if (response.success) {
        // Reload teams to get the updated list
        await loadTeams();
        setShowTeamForm(false);
        setEditingTeam(null);
        // Reset form
        setTeamFormData({
          name: '',
          shortName: '',
          logo: '',
          ageGroups: [],
          coachManagerName: '',
          contactDetails: '',
          sports: 'Football',
          status: 'active'
        });
        setLogoError(null);
        setIsLogoUploading(false);
        setLogoUploadProgress(0);
        setLogoUploadSuccess(false);
        setOriginalFormData({ // Reset original data
          name: '',
          shortName: '',
          logo: '',
          ageGroups: [],
          coachManagerName: '',
          contactDetails: '',
          sports: 'Football',
          status: 'active'
        });
      }
    } catch (error: any) {
      console.error('Failed to update team:', error);
      setSubmitError(error.message || 'Failed to update team. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const createNewTeam = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Get user's club IDs
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      
      if (clubIds.length === 0) {
        throw new Error('No club access found for this user. Please contact your administrator.');
      }

      // Use the first club ID
      const clubId = clubIds[0];

      // Prepare team data for API
      const teamData: CreateTeamRequest = {
        name: teamFormData.name,
        shortName: teamFormData.shortName,
        logo: teamFormData.logo,
        managerName: teamFormData.coachManagerName,
        countryCode: 91, // Default to India
        managerContactNum: parseInt(teamFormData.contactDetails.replace(/\D/g, '')) || 0,
        location: 'Bengaluru', // Default location
        ageCategory: teamFormData.ageGroups
      };

      // Call API to create team
      const response = await createTeam(clubId, teamData);

      if (response.success) {
        // Reload teams to get the updated list
        await loadTeams();
    setShowTeamForm(false);
    setEditingTeam(null);
        // Reset form
        setTeamFormData({
          name: '',
          shortName: '',
          logo: '',
          ageGroups: [],
          coachManagerName: '',
          contactDetails: '',
          sports: 'Football',
          status: 'active'
        });
        setLogoError(null);
        setIsLogoUploading(false);
        setLogoUploadProgress(0);
        setLogoUploadSuccess(false);
        setOriginalFormData({ // Reset original data
          name: '',
          shortName: '',
          logo: '',
          ageGroups: [],
          coachManagerName: '',
          contactDetails: '',
          sports: 'Football',
          status: 'active'
        });
      } else {
        setSubmitError(response.message);
      }
    } catch (error: any) {
      console.error('Failed to create team:', error);
      setSubmitError(error.message || 'Failed to create team. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelTeam = () => {
    setShowTeamForm(false);
    setEditingTeam(null);
    setTeamFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: '',
      sports: 'Football',
      status: 'active'
    });
    setLogoError(null);
    setIsLogoUploading(false);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);
    setOriginalFormData({ // Reset original data
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: '',
      sports: 'Football',
      status: 'active'
    });
  };

  const handleTeamFormChange = (field: string, value: string) => {
    setTeamFormData(prev => ({ ...prev, [field]: value }));
  };

  // Phone number validation
  const validateContactNumber = (number: string): boolean => {
    const phoneRegex = /^[0-9]{10,15}$/;
    return number === '' || phoneRegex.test(number.replace(/\s/g, ''));
  };

  // Check if form is valid (all required fields are filled)
  const isFormValid = () => {
    return teamFormData.name.trim() !== '' &&
           teamFormData.shortName.trim() !== '' &&
           teamFormData.ageGroups.length > 0 &&
           teamFormData.contactDetails.trim() !== '' &&
           validateContactNumber(teamFormData.contactDetails);
  };

  // Function to check if form data has changed from original
  const hasFormChanged = () => {
    if (!editingTeam) return true; // For new teams, always allow save
    
    return (
      teamFormData.name !== originalFormData.name ||
      teamFormData.shortName !== originalFormData.shortName ||
      teamFormData.logo !== originalFormData.logo ||
      JSON.stringify(teamFormData.ageGroups.sort()) !== JSON.stringify(originalFormData.ageGroups.sort()) ||
      teamFormData.coachManagerName !== originalFormData.coachManagerName ||
      teamFormData.contactDetails !== originalFormData.contactDetails ||
      teamFormData.sports !== originalFormData.sports ||
      teamFormData.status !== originalFormData.status
    );
  };

  const handleTeamLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleTeamLogoUpload = async (file: File) => {
    // Validate file
    if (!file.type.startsWith('image/')) {
      setLogoError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setLogoError('Image size should be less than 5MB.');
      return;
    }

    setLogoError(null);
    setIsLogoUploading(true);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);

    try {
      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        onProgress: (progress) => setLogoUploadProgress(progress)
      });

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `team_logo_${Date.now()}.${ext}`;
      const context = `my-club/team-logo/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context,
        }
      );

      const presignedUrl = presignRes.data?.presignedUrl;
      const s3Key = presignRes.data?.key;

      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload image to S3');
      }

      // Update form data with new logo
      setTeamFormData(prev => ({ ...prev, logo: s3Key }));
      setLogoUploadSuccess(true);
      
    } catch (err: any) {
      setLogoError('Logo upload failed. Please try again.');
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleTeamLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await handleTeamLogoUpload(file);
  };

  const handleViewTeam = (team: Team) => {
    // Navigate to TeamDetail page for viewing
    navigate(`/my-club/team-information/${team._id}`, {
      state: { team, mode: 'view' }
    });
  };

  const handleDeleteTeam = (teamId: string) => {
    const team = teams.find(t => t._id === teamId);
    if (team) {
      // Allow deletion if team has no players or only unverified players
      if (team.players === 0) {
        // No players - safe to delete
        setDeleteError(null);
        setDeleteModalTeam(team);
        setShowDeleteModal(true);
      } else if (team.verifiedPlayers === 0) {
        // Has players but none are verified - safe to delete
        setDeleteError(null);
        setDeleteModalTeam(team);
        setShowDeleteModal(true);
      } else {
        // Has verified players - prevent deletion
        setDeleteError(`Cannot delete team "${team.name}" because it has ${team.verifiedPlayers} verified players. Please remove all verified players first.`);
        setShowDeleteModal(true);
        setDeleteModalTeam(team);
      }
    }
  };

  const confirmDeleteTeam = async () => {
    if (deleteModalTeam) {
      try {
        console.log('=== Starting delete team process ===');
        setIsDeleting(true);
        setDeleteError(null);
        
        console.log('Attempting to delete team:', deleteModalTeam);
        
        const success = await deleteTeam(deleteModalTeam._id);
        console.log('Delete team result:', success);
        console.log('Success type:', typeof success);
        
        // If we get here without an exception, consider it successful
        // since deleteTeam will throw an exception for actual errors
        
        // Close modal first
        setShowDeleteModal(false);
        setDeleteModalTeam(null);
        setDeleteError(null);
        
        // Refresh teams data instead of reloading page
        await loadTeams();
        
      } catch (error: any) {
        console.error('❌ Exception caught in confirmDeleteTeam:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        // Show specific error message from API
        let errorMessage = 'Failed to delete team. Please try again.';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.statusMessage) {
            errorMessage = errorData.statusMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        console.log('Setting error message:', errorMessage);
        setDeleteError(errorMessage);
      } finally {
        console.log('=== Finishing delete team process ===');
        setIsDeleting(false);
      }
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteModalTeam(null);
    setDeleteError(null);
  };

  const handleAddPlayers = (teamId: string) => {
    // Navigate to TeamDetail page with add player mode
    const team = teams.find(t => t._id === teamId);
    navigate(`/my-club/team-information/${teamId}`, {
      state: { 
        team, 
        source: 'add-player',
        collapseTeamForm: true // Collapse team form by default
      }
    });
    
    // Scroll to Add Player form after navigation
    setTimeout(() => {
      const addPlayerForm = document.querySelector('.player-form-section');
      if (addPlayerForm) {
        addPlayerForm.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 500);
  };

  const handleManagePlayers = (teamId: string) => {
    // Navigate to TeamDetail page with manage players mode
    const team = teams.find(t => t._id === teamId);
    navigate(`/my-club/team-information/${teamId}`, {
      state: { 
        team, 
        source: 'manage-players',
        collapseTeamForm: true // Collapse team form by default
      }
    });
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Helper functions for S3 upload
  const getCompressionOptions = (file: File) => {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 1) {
      return {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        initialQuality: 0.8
      };
    }
    return null;
  };

  const handleGoBack = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="team-information-page">
        <Container fluid>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs
              items={[
                { label: 'My Club' },
                { label: 'Team Information', active: true }
              ]}
            />
          </div>

          {/* Shimmer loader for teams table */}
          <div className="teams-section-container">
            <div className="mb-4">
              <div className="shimmer-text" style={{ width: '200px', height: '24px', marginBottom: '8px' }}></div>
              <div className="shimmer-text" style={{ width: '300px', height: '16px' }}></div>
            </div>
            <ShimmerLoader type="table" rows={5} />
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-information-page">
        <Container fluid>
          <div className="error-container">
            <div className="alert alert-danger" role="alert">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
            <Button variant="primary" onClick={loadTeams}>
              <i className="ri-refresh-line me-2"></i>
              Retry
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="team-information-page">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs
            items={[
              { label: 'My Club' },
              { label: 'Team Information', active: true }
            ]}
          />
        </div>

        <Row>
          <Col>
            {/* Teams Section */}
            <div className="teams-section-container">
              <TeamsHeader
                title={`Teams (${teams.length})`}
                description="Manage your club teams and view team details"
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                ageGroupFilter={ageGroupFilter}
                onAgeGroupFilterChange={setAgeGroupFilter}
                onAddTeam={handleAddTeam}
                addButtonText="Add New Team"
                showFilters={true}
                ageCategories={ageCategories}
              />
              
              {/* Team Form - Show between Teams header and Team Lineups Card */}
              {showTeamForm && (
                <Card className="team-form-card mb-4">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">{editingTeam ? 'Edit Team Details' : 'Add Team Details'}</h5>
                      <Button 
                        variant="link" 
                        className="close-form-btn"
                        onClick={handleCancelTeam}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>

                    {isEditLoading ? (
                      <div className="edit-loading-container">
                        <ShimmerLoader type="form" />
                      </div>
                    ) : (
                      <>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Team Logo</Form.Label>
                          <div className="team-logo-upload-section">
                            <div className="team-logo-upload">
                              <div 
                                className="team-logo-preview"
                                style={{
                                  background: teamFormData.logo ? `url(${getImageUrl(teamFormData.logo)}) center/cover` : '#f8f9fa'
                                }}
                                onClick={handleTeamLogoClick}
                              >
                                {!teamFormData.logo && (
                                  <div className="upload-placeholder">
                                    <i className="ri-team-line"></i>
                                    <div className="small">Upload Team Logo</div>
                                  </div>
                                )}
                                {isLogoUploading && (
                                  <div className="upload-overlay">
                                    <div className="spinner-border spinner-border-sm text-light" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <div className="small mt-2 text-light">Uploading... {Math.round(logoUploadProgress)}%</div>
                                  </div>
                                )}
                              </div>
                              {teamFormData.logo && (
                                <div 
                                  className="edit-icon"
                                  onClick={handleTeamLogoClick}
                                >
                                  <i className="ri-pencil-line"></i>
                                </div>
                              )}
                              <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/*"
                                onChange={handleTeamLogoFileChange}
                                style={{ display: 'none' }}
                              />
                            </div>
                            
                            {/* Logo upload error and success */}
                            {logoError && (
                              <div className="document-upload-error">
                                <i className="ri-error-warning-line"></i>
                                {logoError}
                              </div>
                            )}
                            
                            {logoUploadSuccess && (
                              <div className="document-upload-success">
                                <i className="ri-check-line"></i>
                                Team logo uploaded successfully!
                              </div>
                            )}
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Team Name<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={teamFormData.name}
                            onChange={(e) => handleTeamFormChange('name', e.target.value)}
                            placeholder="Enter team name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Abbreviation<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={teamFormData.shortName}
                            onChange={(e) => {
                              // Only allow alphabets and limit to 6 characters
                              const value = e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 6);
                              handleTeamFormChange('shortName', value);
                            }}
                            placeholder="Enter abbreviation (alphabets only, max 6 chars)"
                            maxLength={6}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Age Categories<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                          <Select
                            isMulti={editingTeam === null}
                            classNamePrefix="react-select"
                            value={editingTeam === null 
                              ? (teamFormData.ageGroups || []).map(age => ({ value: age, label: age }))
                              : teamFormData.ageGroups && teamFormData.ageGroups.length > 0 
                                ? { value: teamFormData.ageGroups[0], label: teamFormData.ageGroups[0] }
                                : null
                            }
                            onChange={(selectedOptions) => {
                              if (editingTeam === null) {
                                // Create mode: multi-select
                                const selectedValues = Array.isArray(selectedOptions) ? selectedOptions.map((option: any) => option.value) : [];
                                setTeamFormData(prev => ({ ...prev, ageGroups: selectedValues }));
                              } else {
                                // Edit mode: single-select
                                const selectedValue = selectedOptions && !Array.isArray(selectedOptions) ? (selectedOptions as any).value : '';
                                setTeamFormData(prev => ({ ...prev, ageGroups: selectedValue ? [selectedValue] : [] }));
                              }
                            }}
                            options={ageCategories.map(age => ({ value: age, label: age }))}
                            placeholder={editingTeam === null ? "Select age categories" : "Select age category"}
                            isDisabled={editingTeam !== null && editingTeam.players > 0}
                            maxMenuHeight={400}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '45px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                boxShadow: 'none'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 99999,
                                maxHeight: 400,
                                overflow: 'hidden'
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 99999 
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: 400,
                                padding: 0,
                                overflowY: 'auto',
                                overflowX: 'hidden'
                              }),
                              multiValue: (base) => ({
                                ...base,
                                backgroundColor: '#e3f2fd',
                                borderRadius: '6px'
                              }),
                              multiValueLabel: (base) => ({
                                ...base,
                                color: '#1976d2',
                                fontWeight: '500'
                              }),
                              multiValueRemove: (base) => ({
                                ...base,
                                color: '#1976d2',
                                ':hover': {
                                  backgroundColor: '#bbdefb',
                                  color: '#1565c0'
                                }
                              })
                                                          }}
                            />
                            {editingTeam !== null && editingTeam.players > 0 && (
                              <div className="form-text text-muted mt-1">
                                <i className="ri-information-line me-1"></i>
                                Age category cannot be changed once players are registered to the team
                              </div>
                            )}
                          </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Coach/Manager Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={teamFormData.coachManagerName}
                            onChange={(e) => handleTeamFormChange('coachManagerName', e.target.value)}
                            placeholder="Enter coach/manager name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Contact Details<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                          <Form.Control
                            type="tel"
                            value={teamFormData.contactDetails}
                            onChange={(e) => handleTeamFormChange('contactDetails', e.target.value)}
                            placeholder="Enter contact number"
                            isInvalid={teamFormData.contactDetails.trim() !== '' && !validateContactNumber(teamFormData.contactDetails)}
                          />
                          {teamFormData.contactDetails.trim() !== '' && !validateContactNumber(teamFormData.contactDetails) && (
                            <Form.Control.Feedback type="invalid">
                              Please enter a valid phone number (10-15 digits)
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    {submitError && (
                      <div className="alert alert-danger mb-3" role="alert">
                        <i className="ri-error-warning-line me-2"></i>
                        {submitError}
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-end">
                      <Button variant="secondary" onClick={handleCancelTeam} className="me-2" disabled={isSubmitting}>
                        Cancel
                      </Button>
                      <Button 
                        variant="primary" 
                        onClick={handleSaveTeam}
                        disabled={!isFormValid() || isSubmitting || (editingTeam !== null && !hasFormChanged())}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : (
                          editingTeam ? 'Update Team' : 'Add Team'
                        )}
                      </Button>
                    </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              )}
              
              {/* Teams Table using Reusable Component */}
              <TeamsTable
                teams={teams}
                loading={loading}
                showCheckboxes={false}
                showAddPlayers={true}
                showManagePlayers={true}
                showViewDetails={false}
                showEdit={true}
                showDelete={true}
                showPaymentStats={false}
                onAddPlayers={handleAddPlayers}
                onManagePlayers={handleManagePlayers}
                onEdit={handleEditTeam}
                onDelete={handleDeleteTeam}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                ageGroupFilter={ageGroupFilter}
                onAgeGroupFilterChange={setAgeGroupFilter}
                showFilters={false}
                showSearch={false}
                title="Team Lineups"
                emptyMessage="No teams found"
                emptySubMessage="No teams available. Create a new team to get started."
                ageCategories={ageCategories}
              />
            </div>
          </Col>
        </Row>
      </Container>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDeleteTeam}
        title="Delete Team"
        message={deleteError || `Are you sure you want to delete "${deleteModalTeam?.name}"? This action cannot be undone.`}
        confirmLabel={isDeleting ? "Deleting..." : "Yes, Delete"}
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />
    </div>
  );
};

export default TeamInformation; 